import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AdminService, { ReportWithDetails } from '../../services/admin/admin.service';

export const ReportsPage: React.FC = () => {
  const { accessToken } = useAuth();
  const [reports, setReports] = useState<ReportWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'reviewed' | 'unreviewed'>('all');
  const [selectedReport, setSelectedReport] = useState<ReportWithDetails | null>(null);

  useEffect(() => {
    fetchReports();
  }, [page, filter]);

  const fetchReports = async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const reviewedFilter =
        filter === 'reviewed' ? true : filter === 'unreviewed' ? false : undefined;
      const data = await AdminService.getReports(accessToken, page, 20, reviewedFilter);
      setReports(data.reports);
      setPagination(data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewReport = async (
    reportId: string,
    action: 'DISMISSED' | 'WARNED' | 'BANNED',
  ) => {
    if (!accessToken) return;

    try {
      await AdminService.reviewReport(accessToken, reportId, action);
      await fetchReports();
      setSelectedReport(null);
      alert(`Report ${action.toLowerCase()} successfully`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to review report');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Reports Management</h1>
        <p style={styles.loading}>Loading reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Reports Management</h1>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports Management</h1>
        <button onClick={fetchReports} style={styles.refreshButton}>
          🔄 Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <button
          onClick={() => setFilter('all')}
          style={{
            ...styles.filterButton,
            ...(filter === 'all' ? styles.filterButtonActive : {}),
          }}
        >
          All Reports
        </button>
        <button
          onClick={() => setFilter('unreviewed')}
          style={{
            ...styles.filterButton,
            ...(filter === 'unreviewed' ? styles.filterButtonActive : {}),
          }}
        >
          Unreviewed
        </button>
        <button
          onClick={() => setFilter('reviewed')}
          style={{
            ...styles.filterButton,
            ...(filter === 'reviewed' ? styles.filterButtonActive : {}),
          }}
        >
          Reviewed
        </button>
      </div>

      {/* Reports List */}
      <div style={styles.reportsList}>
        {reports.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No reports found</p>
          </div>
        ) : (
          reports.map((report) => (
            <div
              key={report.id}
              style={{
                ...styles.reportCard,
                ...(report.reviewed ? styles.reportCardReviewed : {}),
              }}
            >
              <div style={styles.reportHeader}>
                <div style={styles.reportReason}>
                  <span style={styles.reasonBadge}>{report.reason}</span>
                  {report.reviewed && (
                    <span style={styles.reviewedBadge}>{report.action}</span>
                  )}
                </div>
                <div style={styles.reportDate}>{formatDate(report.createdAt)}</div>
              </div>

              <div style={styles.reportBody}>
                <div style={styles.reportSection}>
                  <div style={styles.sectionLabel}>Reporter:</div>
                  <div style={styles.sectionValue}>
                    {report.reporter.email}
                    {report.reporter.banned && (
                      <span style={styles.bannedTag}>BANNED</span>
                    )}
                  </div>
                </div>

                <div style={styles.reportSection}>
                  <div style={styles.sectionLabel}>Reported User:</div>
                  <div style={styles.sectionValue}>
                    <Link
                      to={`/admin/users/${report.reportedId}`}
                      style={styles.userLink}
                    >
                      {report.reported.email}
                    </Link>
                    {report.reported.banned && (
                      <span style={styles.bannedTag}>BANNED</span>
                    )}
                  </div>
                </div>

                <div style={styles.reportSection}>
                  <div style={styles.sectionLabel}>User Stats:</div>
                  <div style={styles.sectionValue}>
                    {report.reported.stats.totalReportsReceived} reports received •{' '}
                    {report.reported.stats.totalSessions} sessions
                  </div>
                </div>
              </div>

              {!report.reviewed && (
                <div style={styles.reportActions}>
                  <button
                    onClick={() => handleReviewReport(report.id, 'DISMISSED')}
                    style={{ ...styles.actionButton, ...styles.actionButtonDismiss }}
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => handleReviewReport(report.id, 'WARNED')}
                    style={{ ...styles.actionButton, ...styles.actionButtonWarn }}
                  >
                    Warn
                  </button>
                  <button
                    onClick={() => handleReviewReport(report.id, 'BANNED')}
                    style={{ ...styles.actionButton, ...styles.actionButtonBan }}
                  >
                    Ban User
                  </button>
                </div>
              )}

              {report.reviewed && (
                <div style={styles.reviewInfo}>
                  Reviewed on {formatDate(report.reviewedAt!)} • Action: {report.action}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              ...styles.paginationButton,
              ...(page === 1 ? styles.paginationButtonDisabled : {}),
            }}
          >
            Previous
          </button>
          <span style={styles.paginationInfo}>
            Page {page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            style={{
              ...styles.paginationButton,
              ...(page === pagination.totalPages ? styles.paginationButtonDisabled : {}),
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  refreshButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  loading: {
    fontSize: '1rem',
    color: '#6b7280',
  },
  error: {
    padding: '1rem',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: 'var(--radius-md)',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  filterButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    color: '#6b7280',
    transition: 'all 0.2s ease',
  },
  filterButtonActive: {
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6',
  },
  reportsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#9ca3af',
  },
  reportCard: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '2px solid #e5e7eb',
  },
  reportCardReviewed: {
    backgroundColor: '#f9fafb',
    opacity: 0.8,
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e5e7eb',
  },
  reportReason: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
  },
  reasonBadge: {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9rem',
    fontWeight: '600',
  },
  reviewedBadge: {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    backgroundColor: '#dcfce7',
    color: '#166534',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem',
    fontWeight: '600',
  },
  reportDate: {
    fontSize: '0.9rem',
    color: '#9ca3af',
  },
  reportBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  reportSection: {
    display: 'flex',
    gap: '0.5rem',
  },
  sectionLabel: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#6b7280',
    minWidth: '140px',
  },
  sectionValue: {
    fontSize: '0.9rem',
    color: '#1e293b',
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  userLink: {
    color: '#3b82f6',
    textDecoration: 'none',
    fontWeight: '500',
  },
  bannedTag: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.75rem',
    fontWeight: '700',
  },
  reportActions: {
    display: 'flex',
    gap: '0.75rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e5e7eb',
  },
  actionButton: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  actionButtonDismiss: {
    backgroundColor: '#e5e7eb',
    color: '#374151',
  },
  actionButtonWarn: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  actionButtonBan: {
    backgroundColor: '#dc2626',
    color: 'white',
  },
  reviewInfo: {
    fontSize: '0.85rem',
    color: '#6b7280',
    fontStyle: 'italic',
    paddingTop: '1rem',
    borderTop: '1px solid #e5e7eb',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '2rem',
  },
  paginationButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    color: '#374151',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  paginationInfo: {
    fontSize: '0.95rem',
    color: '#6b7280',
  },
};
